import React from "react";
import { useState, useEffect } from 'react';
import {
    FormControl,
    List,
    ListItem,
    MenuItem,
    CircularProgress,
    Alert,
    TextField,
    Pagination,
    InputAdornment,
    Button,
    Tooltip,
    Switch
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';
import { SECRETS_ENV } from "../../../../Config/firebase.ts";
import { RootState, useAppSelector } from '../../../../state/state.tsx';
import dayjs from 'dayjs';
import { SubscriptionDocWithId, Slot } from '../../types/SubscriptionType';
import { ErrorBoundary } from "react-error-boundary";
import { toast } from 'react-toastify';
import SearchIcon from '@mui/icons-material/Search';
import AttendanceCalendar from '../SubscriptionDetails/Components/AttendanceCalendar';
import { selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2.ts';
import AttendanceDurationDialog from '../../../../app/Common/Components/AttendanceDurationDialog.tsx';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import NotesIcon from '@mui/icons-material/Notes';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { fetchSlotsFromFirestore } from '../../../../state/SlotsSlice/SlotsSlice';

interface AttendanceStatus {
    name: string;
    status: string;
    id: string;
    livesubId: string;
    isAttendanceSet: boolean;
    remainingCount?: number;
    subscriptionType?: string;
    billingEnd?: {
        day: number;
        month: number;
        year: number;
    };
    duration?: number;
    isMarkUp?: boolean; // Direct field from API response
    logs: {
        date: string;
        duration: number;
        comments: string;
        logTime?: {
            _seconds: number;
            _nanoseconds: number;
        } | string;
        updatedBy?: string;
        isMarkUp?: boolean;
        slotName?: string;
    }[];
}

const AttendanceSummaryContent = () => {
    const maxDate = dayjs(); // Today's date
    const [selectedDate, setSelectedDate] = useState(maxDate);
    const [selectedSubscription, setSelectedSubscription] = useState('');
    const [attendanceList, setAttendanceList] = useState<AttendanceStatus[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const itemsPerPage = 50;

    const API_ENDPOINT = SECRETS_ENV.API_END_POINT;
    const providerId = useAppSelector(selectEffectiveUID);  
    const displayName = useAppSelector((state: RootState) => state.userSliceV2.data)?.display_name;
    const uid = useAppSelector((state: RootState) => state.userSliceV2.data)?.uid;
    const subscriptions: SubscriptionDocWithId[] = useAppSelector((state: RootState) => state.subscriptionTableSlice.data) || [];

    const [showDurationDialog, setShowDurationDialog] = useState(false);
    const [duration, setDuration] = useState(1);
    const [comments, setComments] = useState('');
    const [isCountHourly, setIsCountHourly] = useState(false);
    const [editedAttendance, setEditedAttendance] = useState<{ [livesubId: string]: { present: boolean, duration: number, comments: string, delta: number, toggle?: boolean, slotName?: string, isMarkUp?: boolean } }>({});
    const [expandedRows, setExpandedRows] = useState<{ [livesubId: string]: boolean }>({});
    const [selectedCalendarUser, setSelectedCalendarUser] = useState<AttendanceStatus | null>(null);
    const [saving, setSaving] = useState(false);
    const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
    const [slots, setSlots] = useState<Slot[]>([]);
    const [slotAssignments, setSlotAssignments] = useState<{ [key: string]: any[] }>({});

    // Set default subscription when subscriptions are loaded
    useEffect(() => {
        const eligibleSubscriptions = subscriptions.filter(
            sub => sub.type !== 'Rentals' && sub.type !== 'PGHostels'
        );
        if (eligibleSubscriptions.length > 0 && !selectedSubscription) {
            setSelectedSubscription(eligibleSubscriptions[0].id);
        }
    }, [subscriptions]);

    // Update isCountHourly when subscription changes
    useEffect(() => {
        const sub = subscriptions.find(sub => sub.id === selectedSubscription);
        setIsCountHourly(!!sub?.isCountHourly);
        // Clear selected calendar user when subscription changes
        setSelectedCalendarUser(null);
    }, [selectedSubscription, subscriptions]);

    // Fetch slots when subscription changes
    useEffect(() => {
        if (selectedSubscription && providerId) {
            fetchSlots();
        }
    }, [selectedSubscription, providerId]);

    // Log selectedCalendarUser when it changes
    useEffect(() => {
        if (selectedCalendarUser) {
            // selectedCalendarUser is set
        }
    }, [selectedCalendarUser]);

    const fetchSlots = async () => {
        if (!providerId || !selectedSubscription) return;
        
        try {
            const result = await fetchSlotsFromFirestore(providerId, selectedSubscription);
            setSlots(result.slots);
            setSlotAssignments(result.assignments);
        } catch (error) {
            console.error('Error fetching slots:', error);
            toast.error('Failed to fetch slots');
        }
    };

    // Function to get the user's assigned slot
    const getUserAssignedSlot = (userId: string) => {
        for (const [slotId, assignments] of Object.entries(slotAssignments)) {
            const assignment = assignments.find((assignment: any) => assignment.studentId === userId);
            if (assignment) {
                const slot = slots.find(s => s.id === slotId);
                return slot?.name || '';
            }
        }
        return '';
    };

    // Function to get the current makeup status from student's logs
    const getCurrentMakeupStatus = (student: AttendanceStatus) => {
        // Use the direct isMarkUp field from API response
        return Boolean(student.isMarkUp);
    };

    // Function to get the current slot name from student's logs
    const getCurrentSlotName = (student: AttendanceStatus) => {
        if (!student.logs || student.logs.length === 0) return '';
        
        // Get the most recent log for today
        const today = selectedDate.format('YYYY-MM-DD');
        const todayLogs = student.logs.filter(log => {
            let logDate = '';
            if (log.date) {
                logDate = log.date;
            } else if (log.logTime) {
                if (typeof log.logTime === 'string') {
                    logDate = new Date(log.logTime).toISOString().split('T')[0];
                } else if (log.logTime._seconds) {
                    logDate = new Date(log.logTime._seconds * 1000).toISOString().split('T')[0];
                }
            }
            return logDate === today;
        });
        
        if (todayLogs.length > 0) {
            // Sort by logTime to get the most recent
            const sortedLogs = todayLogs.sort((a, b) => {
                const timeA = typeof a.logTime === 'string' ? new Date(a.logTime).getTime() : 
                             a.logTime?._seconds ? a.logTime._seconds * 1000 : 0;
                const timeB = typeof b.logTime === 'string' ? new Date(b.logTime).getTime() : 
                             b.logTime?._seconds ? b.logTime._seconds * 1000 : 0;
                return timeB - timeA;
            });
            
            return sortedLogs[0].slotName || '';
        }
        
        return '';
    };

    // Fetch attendance whenever subscription or date changes
    useEffect(() => {
        if (selectedSubscription) {
            fetchAttendance();
        }
    }, [selectedSubscription, selectedDate]);

    const fetchAttendance = async (isBackgroundRefresh = false) => {
        if (!isBackgroundRefresh) {
            setLoading(true);
        }
        setError(null);
        try {
            const sub = subscriptions.find(sub => sub.id === selectedSubscription);
            const isCountHourlyFlag = !!sub?.isCountHourly;
            const subscriptionType = sub?.subscriptionType || '';
            const response = await axios.post(`${API_ENDPOINT}/api/getattendanceinsub`, {
                subscriptionId: selectedSubscription,
                providerId,
                year: selectedDate.year(),
                month: selectedDate.month() + 1,
                day: selectedDate.date(),
                isCountHourly: isCountHourlyFlag,
                subscriptionType: subscriptionType
            });
            if (response.data.success) {
                setAttendanceList(response.data.attendanceStatus);
                if (!isBackgroundRefresh) {
                    setEditedAttendance({}); // Reset edits on fetch
                }
                // Increment refresh key to force calendar re-render
                if (isBackgroundRefresh) {
                    setCalendarRefreshKey(prev => prev + 1);
                }
            }
        } catch (error) {
            setError('Failed to fetch attendance data');
            toast.error('Failed to fetch attendance data');
        } finally {
            if (!isBackgroundRefresh) {
                setLoading(false);
            }
        }
    };

    // Filter and paginate attendance list
    const filteredAttendanceList = attendanceList.filter(student =>
        student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.status.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const paginatedAttendanceList = filteredAttendanceList.slice(
        (page - 1) * itemsPerPage,
        page * itemsPerPage
    );

    const totalPages = Math.ceil(filteredAttendanceList.length / itemsPerPage);

    // Get the selected subscription data
    const selectedSubscriptionData = subscriptions.find(sub => sub.id === selectedSubscription);

    // Handler for editing attendance fields
    const handleEditAttendance = (livesubId: string, field: 'duration' | 'comments' | 'delta' | 'toggle' | 'slotName' | 'isMarkUp', value: any) => {
        setEditedAttendance(prev => {
            // Get the current state
            const currentState = prev[livesubId] || {};
            const student = attendanceList.find(s => s.livesubId === livesubId);
            
            // Determine current status (from state or original data)
            const currentStatus = currentState.toggle !== undefined ? currentState.toggle : student?.isAttendanceSet ?? false;
            
            // Create base state with all current values
            const baseState = {
                present: currentState.present ?? true,
                duration: currentState.duration ?? 0,
                comments: currentState.comments ?? '',
                delta: currentState.delta ?? 0,
                slotName: currentState.slotName ?? '',
                isMarkUp: currentState.isMarkUp ?? getCurrentMakeupStatus(student),
                toggle: currentState.toggle !== undefined ? currentState.toggle : student?.isAttendanceSet ?? false
            };

            // Handle status change to absent
            if (field === 'toggle' && !value) {
                return {
                    ...prev,
                    [livesubId]: {
                        ...baseState,
                        toggle: false,
                        isMarkUp: false // Force markup off when absent
                    }
                };
            }

            // Handle markup change
            if (field === 'isMarkUp') {
                // For count hourly subscriptions, allow markup regardless of status
                if (!isCountHourly && value && !currentStatus) {
                    toast.warning('Cannot mark as makeup when status is absent. Please set status to present first.');
                    return prev;
                }
                return {
                    ...prev,
                    [livesubId]: {
                        ...baseState,
                        isMarkUp: value,
                        toggle: currentStatus // Preserve current status
                    }
                };
            }

            // Handle all other changes
            return {
                ...prev,
                [livesubId]: {
                    ...baseState,
                    [field]: value
                }
            };
        });
    };

    // Handler for batch save
    const handleBatchSave = async () => {
        const changedUsers = Object.entries(editedAttendance)
            .map(([livesubId, edit]) => {
                const student = attendanceList.find(s => s.livesubId === livesubId);
                if (!student) return null;
                
                if (isCountHourly) {
                    // For isCountHourly subscriptions, send duration and comments
                    const delta = edit.delta || 0;
                    const comments = edit.comments || '';
                    return {
                        userId: student.id,
                        livesubId: student.livesubId,
                        present: true,
                        comments: comments,
                        duration: delta,
                        logTime: new Date().toISOString(),
                        updatedBy: displayName,
                        updaterId: uid,
                        status: 'present',
                        slotName: edit.slotName || undefined,
                        isMarkUp: edit.isMarkUp || false
                    };
                } else {
                    // For non-hourly subscriptions, handle toggle attendance
                    const isPresent = edit.toggle !== undefined ? edit.toggle : student.isAttendanceSet;
                    const comments = edit.comments || '';
                    return {
                        userId: student.id,
                        livesubId: student.livesubId,
                        present: isPresent,
                        comments: comments,
                        logTime: new Date().toISOString(),
                        updatedBy: displayName,
                        updaterId: uid,
                        status: isPresent ? 'present' : 'absent',
                        slotName: edit.slotName || undefined,
                        isMarkUp: edit.isMarkUp || false
                    };
                }
            })
            .filter(user => user && (
                isCountHourly ? (user.duration !== 0 || user.comments !== '') : true
            ));
            
        if (changedUsers.length === 0) {
            toast.info('No changes to save.');
            return;
        }
        
        setSaving(true);

        // Keep a copy of current edited state
        const previousState = { ...editedAttendance };
        
        try {
            // Make the API call
            const response = await axios.post(`${API_ENDPOINT}/api/setAttendanceForListUsers`, {
                providerId,
                subscriptionId: selectedSubscription,
                year: selectedDate.year(),
                month: selectedDate.month() + 1,
                day: selectedDate.date(),
                attendanceList: JSON.stringify(changedUsers),
                isCountHourly: isCountHourly
            });

            // Only clear edited state after successful save
            setEditedAttendance({});
            toast.success('Attendance updated successfully!');

            // Fetch updated data without UI flicker
            const updatedResponse = await axios.post(`${API_ENDPOINT}/api/getattendanceinsub`, {
                subscriptionId: selectedSubscription,
                providerId,
                year: selectedDate.year(),
                month: selectedDate.month() + 1,
                day: selectedDate.date(),
                isCountHourly: isCountHourly,
                subscriptionType: selectedSubscriptionData?.subscriptionType || ''
            });

            if (updatedResponse.data.success) {
                setAttendanceList(updatedResponse.data.attendanceStatus);
                setCalendarRefreshKey(prev => prev + 1);
            }
        } catch (error) {
            // On error, restore previous state
            setEditedAttendance(previousState);
            toast.error('Failed to update attendance');
        } finally {
            setSaving(false);
        }
    };

    const toggleRow = (livesubId: string) => {
        setExpandedRows(prev => {
            const newState = {
                ...prev,
                [livesubId]: !prev[livesubId]
            };
            return newState;
        });
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
            <div className="lg:col-span-7 p-4 lg:p-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                            />
                        </svg>
                    </div>
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-800">Daily Attendance</h1>
                        <p className="text-sm text-gray-500 mt-1">Track and manage attendance for your subscriptions</p>
                        {saving && (
                            <div className="flex items-center gap-2 mt-2 text-sm text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100">
                                <CircularProgress size={16} sx={{ color: 'rgb(37 99 235)' }} />
                                <span className="font-medium">Saving changes...</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="space-y-4 max-w-xl">
                    <div className="grid grid-cols-2 gap-4">
                        <FormControl fullWidth>
                            <TextField
                                select
                                size="small"
                                label="Select Subscription"
                                value={selectedSubscription}
                                onChange={(e) => setSelectedSubscription(e.target.value)}
                                className="bg-white"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '0.75rem',
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgb(37 99 235)'
                                        }
                                    }
                                }}
                            >
                                {subscriptions
                                    .filter(sub => sub.type !== 'Rentals' && sub.type !== 'PGHostels')
                                    .map((sub) => (
                                        <MenuItem key={sub.id} value={sub.id}>
                                            {sub.Name}
                                        </MenuItem>
                                    ))}
                            </TextField>
                        </FormControl>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Select Date"
                                value={selectedDate}
                                onChange={(newValue) => newValue && setSelectedDate(newValue)}
                                maxDate={maxDate}
                                format="DD/MM/YYYY"
                                className="bg-white"
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '0.75rem',
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgb(37 99 235)'
                                                }
                                            }
                                        }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </div>

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search students..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setPage(1);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon className="text-gray-400" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '0.75rem',
                                backgroundColor: 'white',
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgb(37 99 235)'
                                }
                            }
                        }}
                    />
                </div>

                {/* Warning for slots subscriptions */}
                {slots.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                        <div className="flex items-start gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <div className="text-sm">
                                <div className="font-medium text-amber-800 mb-1">Makeup Classes Across Slots</div>
                                <div className="text-amber-700">
                                    Makeup classes can be marked across different time slots. Use this feature wisely to maintain accurate attendance records.
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {error && (
                    <Alert
                        severity="error"
                        className="mt-4"
                        sx={{
                            borderRadius: '0.75rem'
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {Object.keys(editedAttendance).length > 0 && (
                    <Alert
                        severity="warning"
                        className="mt-4"
                        sx={{
                            borderRadius: '0.75rem'
                        }}
                        action={
                            <Button 
                                variant="contained" 
                                color="primary" 
                                size="small"
                                onClick={handleBatchSave}
                                disabled={saving}
                                sx={{
                                    borderRadius: '0.5rem',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    minWidth: 100,
                                    px: 2
                                }}
                            >
                                {saving ? (
                                    <>
                                        <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Now'
                                )}
                            </Button>
                        }
                    >
                        <strong>Unsaved Changes:</strong> You have made changes to attendance. Please save your changes or they will be lost when you navigate away.
                    </Alert>
                )}

                {loading ? (
                    <div className="flex justify-center items-center h-[400px]">
                        <CircularProgress sx={{ color: 'rgb(37 99 235)' }} />
                    </div>
                ) : attendanceList.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-[400px] text-center">
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Students Found</h3>
                        <p className="text-gray-500 mb-1">Apparently none is in this batch :-)</p>
                        <p className="text-sm text-gray-400">Try selecting a different date or subscription</p>
                    </div>
                ) : (
                    <>
                        <div className="mt-6">
                            <List className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
                                {paginatedAttendanceList.map((student) => {
                                    const logged = student.duration || 0;
                                    const delta = student.livesubId && editedAttendance[student.livesubId]?.delta !== undefined
                                        ? editedAttendance[student.livesubId].delta
                                        : 0;
                                    const logs = Array.isArray(student.logs) ? student.logs : [];

                                    const comments = student.livesubId && editedAttendance[student.livesubId]?.comments !== undefined
                                        ? editedAttendance[student.livesubId].comments
                                        : '';
                                    const isExpanded = !!expandedRows[student.livesubId];
                                    const isCountHourly = !!selectedSubscriptionData?.isCountHourly;
                                    return (
                                        <React.Fragment key={student.livesubId}>
                                            <ListItem
                                                className="p-0 transition-all duration-200 hover:bg-gray-50"
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <div className="w-full">
                                                    {/* Main Row */}
                                                    <div className="flex flex-col gap-4 p-4">
                                                        {/* First Row - Student Info and Status */}
                                                        <div className="flex items-center justify-between">
                                                            {/* Left Section - Student Info */}
                                                            <div className="flex items-center gap-3 flex-1 min-w-0" onClick={() => toggleRow(student.livesubId)}>
                                                                {/* Avatar */}
                                                                <div className="flex-shrink-0">
                                                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
                                                                        <span className="text-white font-semibold text-sm">
                                                                            {student.name.charAt(0).toUpperCase()}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                {/* Student Details */}
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex flex-col gap-1">
                                                                        <h3 className="text-base font-semibold text-gray-900" title={student.name}>
                                                                            {student.name}
                                                                        </h3>
                                                                        <div className="flex flex-wrap gap-1">
                                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                                                student.status === 'Active' ? 'bg-green-100 text-green-700 border border-green-200' :
                                                                                student.status === 'Paused' ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' :
                                                                                'bg-red-100 text-red-700 border border-red-200'
                                                                            }`}>
                                                                                {student.status}
                                                                            </span>
                                                                            {(selectedSubscriptionData?.subscriptionType === 'Count' || selectedSubscriptionData?.isCountHourly) && student.remainingCount !== undefined && (
                                                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                                                                                    student.remainingCount <= 0 
                                                                                        ? 'bg-red-100 text-red-700 border-red-200' 
                                                                                        : 'bg-orange-100 text-orange-700 border-orange-200'
                                                                                }`}>
                                                                                    {student.remainingCount <= 0 
                                                                                        ? `Recharge! (${student.remainingCount < 0 ? 'Overflow' : '0'})` 
                                                                                        : `${student.remainingCount % 1 === 0 ? student.remainingCount : student.remainingCount.toFixed(2)} left`
                                                                                    }
                                                                                </span>
                                                                            )}
                                                                            {logs.length > 0 && (
                                                                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                                                                                    {logs.length} entries
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Right Section - Current Status Display */}
                                                            {isCountHourly && (
                                                                <div className="text-center bg-gray-50 rounded-lg p-2 flex-shrink-0">
                                                                    <div className="text-xs text-gray-500">Today's Log</div>
                                                                    <div className={`text-lg font-bold ${
                                                                        logged > 0 ? 'text-green-600' : 'text-gray-400'
                                                                    }`}>
                                                                        {logged > 0 ? `${logged}h` : '0h'}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {!isCountHourly && (
                                                                <div className="text-center bg-gray-50 rounded-lg p-2 flex-shrink-0">
                                                                    <div className="text-xs text-gray-500">Today's Status</div>
                                                                    <div className={`text-lg font-bold ${
                                                                        (editedAttendance[student.livesubId]?.toggle !== undefined ? editedAttendance[student.livesubId].toggle : student.isAttendanceSet) ? 'text-green-600' : 'text-gray-400'
                                                                    }`}>
                                                                        {(editedAttendance[student.livesubId]?.toggle !== undefined ? editedAttendance[student.livesubId].toggle : student.isAttendanceSet) ? 'Present' : 'Absent'}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Second Row - Controls */}
                                                        <div className="flex flex-wrap items-center gap-3">
                                                            {/* Expand/Collapse Indicator */}
                                                            {logs.length > 0 && (
                                                                <div className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors cursor-pointer" onClick={() => toggleRow(student.livesubId)}>
                                                                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                                    <span>{isExpanded ? 'Hide logs' : 'View logs'}</span>
                                                                </div>
                                                            )}

                                                            {/* Edit Controls for Count Hourly */}
                                                            {isCountHourly && (
                                                                <>
                                                                    {/* Duration Controls */}
                                                                    <div className="flex items-center gap-1">
                                                                        <Button
                                                                            variant="outlined"
                                                                            size="small"
                                                                            onClick={() => handleEditAttendance(student.livesubId, 'delta', Math.max(-24, (editedAttendance[student.livesubId]?.delta || 0) - 0.5))}
                                                                            sx={{
                                                                                minWidth: '32px',
                                                                                height: '36px',
                                                                                borderRadius: '8px 0 0 8px',
                                                                                borderColor: '#d1d5db',
                                                                                color: '#6b7280',
                                                                                '&:hover': {
                                                                                    borderColor: '#9ca3af',
                                                                                    backgroundColor: '#f9fafb'
                                                                                }
                                                                            }}
                                                                        >
                                                                            <RemoveIcon fontSize="small" />
                                                                        </Button>
                                                                        
                                                                        <TextField
                                                                            type="number"
                                                                            label="Hours"
                                                                            value={delta}
                                                                            onChange={e => {
                                                                                const value = e.target.value;
                                                                                const numValue = value === '' ? 0 : parseFloat(value);
                                                                                handleEditAttendance(student.livesubId, 'delta', isNaN(numValue) ? 0 : numValue);
                                                                            }}
                                                                            size="small"
                                                                            style={{ width: 80, minWidth: 80 }}
                                                                            inputProps={{ 
                                                                                step: 0.5,
                                                                                min: -24,
                                                                                max: 24,
                                                                                style: { textAlign: 'center', fontSize: '12px' }
                                                                            }}
                                                                            sx={{
                                                                                '& .MuiOutlinedInput-root': {
                                                                                    borderRadius: '0',
                                                                                    height: '36px',
                                                                                    '& .MuiInputLabel-root': {
                                                                                        fontSize: '12px'
                                                                                    },
                                                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                                        borderColor: 'rgb(37 99 235)'
                                                                                    }
                                                                                }
                                                                            }}
                                                                        />
                                                                        
                                                                        <Button
                                                                            variant="outlined"
                                                                            size="small"
                                                                            onClick={() => handleEditAttendance(student.livesubId, 'delta', Math.min(24, (editedAttendance[student.livesubId]?.delta || 0) + 0.5))}
                                                                            sx={{
                                                                                minWidth: '32px',
                                                                                height: '36px',
                                                                                borderRadius: '0 8px 8px 0',
                                                                                borderColor: '#d1d5db',
                                                                                color: '#6b7280',
                                                                                '&:hover': {
                                                                                    borderColor: '#9ca3af',
                                                                                    backgroundColor: '#f9fafb'
                                                                                }
                                                                            }}
                                                                        >
                                                                            <AddIcon fontSize="small" />
                                                                        </Button>
                                                                    </div>
                                                                </>
                                                            )}

                                                            {/* Status Selection for Non-Hourly */}
                                                            {!isCountHourly && (
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs font-medium text-gray-600">Status</span>
                                                                    <TextField
                                                                        select
                                                                        value={(editedAttendance[student.livesubId]?.toggle !== undefined ? editedAttendance[student.livesubId].toggle : student.isAttendanceSet) ? 'present' : 'absent'}
                                                                        onChange={(e) => handleEditAttendance(student.livesubId, 'toggle', e.target.value === 'present')}
                                                                        size="small"
                                                                        style={{ width: 100, minWidth: 100 }}
                                                                        sx={{
                                                                            '& .MuiOutlinedInput-root': {
                                                                                borderRadius: '8px',
                                                                                height: '36px',
                                                                                fontSize: '12px',
                                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                                    borderColor: 'rgb(37 99 235)'
                                                                                }
                                                                            }
                                                                        }}
                                                                    >
                                                                        <MenuItem value="present">
                                                                            <div className="flex items-center gap-2">
                                                                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                                                <span className="text-sm">Present</span>
                                                                            </div>
                                                                        </MenuItem>
                                                                        <MenuItem value="absent">
                                                                            <div className="flex items-center gap-2">
                                                                                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                                                                <span className="text-sm">Absent</span>
                                                                            </div>
                                                                        </MenuItem>
                                                                    </TextField>
                                                                </div>
                                                            )}

                                                            {/* Comments Field */}
                                                            <TextField
                                                                label="Comments"
                                                                value={comments}
                                                                onChange={e => handleEditAttendance(student.livesubId, 'comments', e.target.value)}
                                                                size="small"
                                                                style={{ width: 150, minWidth: 150 }}
                                                                multiline
                                                                rows={1}
                                                                InputProps={{
                                                                    startAdornment: <NotesIcon fontSize="small" sx={{ mr: 1, color: 'gray.500' }} />
                                                                }}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: '8px',
                                                                        fontSize: '12px',
                                                                        '& .MuiInputLabel-root': {
                                                                            fontSize: '12px'
                                                                        },
                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: 'rgb(37 99 235)'
                                                                        }
                                                                    }
                                                                }}
                                                            />

                                                            {/* Slot Selection */}
                                                            {slots.length > 0 && (
                                                                <div className="flex flex-col gap-1">
                                                                    <span className="text-xs font-medium text-gray-600">Slot</span>
                                                                    <div className="flex flex-wrap gap-1">
                                                                        <Button
                                                                            variant={(() => {
                                                                                const assignedSlot = getUserAssignedSlot(student.id);
                                                                                const currentSlot = getCurrentSlotName(student);
                                                                                const editedSlot = editedAttendance[student.livesubId]?.slotName;
                                                                                const selectedSlot = editedSlot !== undefined ? editedSlot : (currentSlot || assignedSlot);
                                                                                return selectedSlot === '' ? 'contained' : 'outlined';
                                                                            })()}
                                                                            size="small"
                                                                            onClick={() => handleEditAttendance(student.livesubId, 'slotName', '')}
                                                                            sx={{
                                                                                borderRadius: '16px',
                                                                                textTransform: 'none',
                                                                                fontSize: '11px',
                                                                                minWidth: '50px',
                                                                                height: '28px',
                                                                                ...(editedAttendance[student.livesubId]?.slotName === '' || (!editedAttendance[student.livesubId]?.slotName && !getCurrentSlotName(student) && !getUserAssignedSlot(student.id)) ? {
                                                                                    backgroundColor: '#f3f4f6',
                                                                                    color: '#6b7280',
                                                                                    '&:hover': {
                                                                                        backgroundColor: '#e5e7eb'
                                                                                    }
                                                                                } : {
                                                                                    borderColor: '#d1d5db',
                                                                                    color: '#6b7280'
                                                                                })
                                                                            }}
                                                                        >
                                                                            No slot
                                                                        </Button>
                                                                        {slots.map((slot) => (
                                                                            <Button
                                                                                key={slot.id}
                                                                                variant={(() => {
                                                                                    const assignedSlot = getUserAssignedSlot(student.id);
                                                                                    const currentSlot = getCurrentSlotName(student);
                                                                                    const editedSlot = editedAttendance[student.livesubId]?.slotName;
                                                                                    const selectedSlot = editedSlot !== undefined ? editedSlot : (currentSlot || assignedSlot);
                                                                                    return selectedSlot === slot.name ? 'contained' : 'outlined';
                                                                                })()}
                                                                                size="small"
                                                                                onClick={() => handleEditAttendance(student.livesubId, 'slotName', slot.name)}
                                                                                sx={{
                                                                                    borderRadius: '16px',
                                                                                    textTransform: 'none',
                                                                                    fontSize: '11px',
                                                                                    minWidth: '50px',
                                                                                    height: '28px',
                                                                                    ...(editedAttendance[student.livesubId]?.slotName === slot.name || (!editedAttendance[student.livesubId]?.slotName && getCurrentSlotName(student) === slot.name) ? {
                                                                                        backgroundColor: '#3b82f6',
                                                                                        color: 'white',
                                                                                        '&:hover': {
                                                                                            backgroundColor: '#2563eb'
                                                                                        }
                                                                                    } : {
                                                                                        borderColor: '#d1d5db',
                                                                                        color: '#6b7280',
                                                                                        '&:hover': {
                                                                                            borderColor: '#9ca3af',
                                                                                            backgroundColor: '#f9fafb'
                                                                                        }
                                                                                    })
                                                                                }}
                                                                            >
                                                                                {slot.name}
                                                                            </Button>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Makeup Toggle */}
                                                            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1">
                                                                <span className="text-xs font-medium text-gray-700">Makeup</span>
                                                                <Tooltip title={
                                                                    isCountHourly 
                                                                        ? "Mark this attendance as a makeup class"
                                                                        : (editedAttendance[student.livesubId]?.toggle !== undefined ? !editedAttendance[student.livesubId].toggle : !student.isAttendanceSet)
                                                                            ? "Makeup can only be marked when status is present"
                                                                            : "Mark this attendance as a makeup class"
                                                                }>
                                                                    <span>
                                                                        <Switch
                                                                            checked={editedAttendance[student.livesubId]?.isMarkUp !== undefined ? editedAttendance[student.livesubId].isMarkUp : getCurrentMakeupStatus(student)}
                                                                            onChange={(e) => handleEditAttendance(student.livesubId, 'isMarkUp', e.target.checked)}
                                                                            size="small"
                                                                            disabled={!isCountHourly && (editedAttendance[student.livesubId]?.toggle !== undefined ? !editedAttendance[student.livesubId].toggle : !student.isAttendanceSet)}
                                                                            sx={{
                                                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                                                    color: '#8b5cf6',
                                                                                    '&:hover': {
                                                                                        backgroundColor: 'rgba(139, 92, 246, 0.08)'
                                                                                    }
                                                                                },
                                                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                                    backgroundColor: '#8b5cf6'
                                                                                },
                                                                                '& .Mui-disabled': {
                                                                                    opacity: 0.4
                                                                                }
                                                                            }}
                                                                        />
                                                                    </span>
                                                                </Tooltip>
                                                            </div>

                                                            {/* Calendar Button */}
                                                            <Tooltip title="View Calendar">
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    sx={{ 
                                                                        borderRadius: '50%', 
                                                                        minWidth: '36px', 
                                                                        width: '36px', 
                                                                        height: '36px',
                                                                        background: '#f8fafc', 
                                                                        borderColor: '#e2e8f0',
                                                                        '&:hover': {
                                                                            background: '#e2e8f0',
                                                                            borderColor: '#cbd5e1'
                                                                        }
                                                                    }}
                                                                    onClick={() => setSelectedCalendarUser(student)}
                                                                >
                                                                    <CalendarMonthIcon fontSize="small" />
                                                                </Button>
                                                            </Tooltip>
                                                        </div>
                                                    </div>

                                                                                                        {/* Expanded Logs Section */}
                                                    {isExpanded && logs.length > 0 && (
                                                        <div className="border-t border-gray-100 bg-gray-50">
                                                            <div className="p-6">
                                                                <div className="flex items-center gap-2 mb-4">
                                                                    <NotesIcon fontSize="small" className="text-gray-600" />
                                                                    <h4 className="font-semibold text-gray-700">Attendance Logs</h4>
                                                                    <span className="text-sm text-gray-500">({logs.length} entries)</span>
                                                                </div>
                                                                
                                                                {/* Header Row */}
                                                                <div className="bg-gray-100 p-3 rounded-lg mb-3">
                                                                    <div className="flex items-center gap-3 text-xs font-medium text-gray-600">
                                                                        <span className="flex-shrink-0" style={{ width: '140px' }}>Time</span>
                                                                        <div className="flex-1">Comments</div>
                                                                        {isCountHourly && (
                                                                            <span className="flex-shrink-0 text-center" style={{ width: '60px' }}>Hours</span>
                                                                        )}
                                                                        <span className="flex-shrink-0 text-center" style={{ width: '80px' }}>Makeup</span>
                                                                        {slots.length > 0 && (
                                                                            <span className="flex-shrink-0 text-center" style={{ width: '80px' }}>Slot</span>
                                                                        )}
                                                                        <span className="flex-shrink-0 text-center" style={{ width: '100px' }}>Updated By</span>
                                                                    </div>
                                                                </div>
                                                                
                                                                <div className="grid gap-3 max-h-64 overflow-y-auto">
                                                                    {logs
                                                                        .sort((a, b) => {
                                                                            // Sort by timestamp descending (newest first)
                                                                            const getTimestamp = (log: any) => {
                                                                                if (log.logTime) {
                                                                                    if (typeof log.logTime === 'string') {
                                                                                        return new Date(log.logTime).getTime();
                                                                                    } else if (log.logTime._seconds) {
                                                                                        return log.logTime._seconds * 1000;
                                                                                    }
                                                                                } else if (log.date) {
                                                                                    return new Date(log.date).getTime();
                                                                                }
                                                                                return 0;
                                                                            };
                                                                            return getTimestamp(b) - getTimestamp(a);
                                                                        })
                                                                        .map((log, idx) => {
                                                                        let timestamp = '-';
                                                                        if (log.logTime) {
                                                                            if (typeof log.logTime === 'string') {
                                                                                timestamp = new Date(log.logTime).toLocaleString();
                                                                            } else if (log.logTime._seconds) {
                                                                                // Firestore Timestamp format
                                                                                const date = new Date(log.logTime._seconds * 1000);
                                                                                timestamp = date.toLocaleString();
                                                                            }
                                                                        } else if (log.date) {
                                                                            timestamp = new Date(log.date).toLocaleString();
                                                                        }
                                                                        
                                                                        return (
                                                                            <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                                                                                <div className="flex items-center gap-3">
                                                                                    <span className="text-xs text-gray-500 font-mono bg-gray-50 px-2 py-1 rounded flex-shrink-0" style={{ width: '140px' }}>
                                                                                        {timestamp}
                                                                                    </span>
                                                                                    
                                                                                    <div className="flex-1">
                                                                                        {log.comments ? (
                                                                                            <div 
                                                                                                className="text-sm text-gray-700 bg-gray-50 px-3 py-1 rounded border-l-3 border-blue-300"
                                                                                                title={log.comments}
                                                                                                style={{
                                                                                    whiteSpace: 'nowrap',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    display: 'inline-block',
                                                                                    maxWidth: '100%'
                                                                                }}
                                                                                            >
                                                                                                "{log.comments}"
                                                                                            </div>
                                                                                        ) : (
                                                                                            <div className="text-sm text-gray-400 italic">No comments</div>
                                                                                        )}
                                                                                    </div>
                                                                                    
                                                                                    {isCountHourly && (
                                                                                        <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded flex-shrink-0 text-center" style={{ width: '60px' }}>
                                                                                            {log.duration}h
                                                                                        </span>
                                                                                    )}

                                                                                    <span className="text-sm font-semibold text-center flex-shrink-0" style={{ width: '80px' }}>
                                                                                        {log.isMarkUp ? (
                                                                                            <span className="px-2 py-1 rounded bg-purple-100 text-purple-700 text-xs">
                                                                                                Makeup
                                                                                            </span>
                                                                                        ) : (
                                                                                            <span className="px-2 py-1 rounded bg-gray-100 text-gray-600 text-xs">
                                                                                                Regular
                                                                                            </span>
                                                                                        )}
                                                                                    </span>

                                                                                    {slots.length > 0 && log.slotName && (
                                                                                        <span className="text-sm text-purple-600 bg-purple-50 px-2 py-1 rounded flex-shrink-0 text-center" style={{ width: '80px' }}>
                                                                                            {log.slotName}
                                                                                        </span>
                                                                                    )}

                                                                                    <span className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded flex-shrink-0 text-center" style={{ width: '100px' }}>
                                                                                        {log.updatedBy || 'Unknown'}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </ListItem>
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        </div>

                        {totalPages > 1 && (
                            <div className="flex justify-center mt-6">
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(_, newPage) => setPage(newPage)}
                                    color="primary"
                                    size="large"
                                    showFirstButton
                                    showLastButton
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            borderRadius: '0.75rem'
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="lg:col-span-5 p-4 lg:p-6 border-l border-gray-200">
                <div className="sticky top-6">
                    {selectedCalendarUser ? (
                        <>
                            <div className="mb-6">
                                <h2 className="text-lg font-semibold">Attendance Calendar for {selectedCalendarUser.name}</h2>
                            </div>
                            <AttendanceCalendar
                                userId={selectedCalendarUser.id}
                                livesubId={selectedCalendarUser.livesubId}
                                subscriptionId={selectedSubscription}
                                userName={selectedCalendarUser.name}
                                subscriptionDays={selectedSubscriptionData?.days ? selectedSubscriptionData.days.map((day: number) => day === 1) : [true, true, true, true, true, true, true]}
                                subscriptionType={selectedSubscriptionData?.subscriptionType || ''}
                                endDate={selectedCalendarUser.billingEnd ? new Date(selectedCalendarUser.billingEnd.year, selectedCalendarUser.billingEnd.month - 1, selectedCalendarUser.billingEnd.day) : undefined}
                                isCountHourly={!!selectedSubscriptionData?.isCountHourly}
                                slots={slots}
                                slotAssignments={slotAssignments}
                                refreshKey={calendarRefreshKey}
                            />
                        </>
                    ) : (
                        <div className="h-[400px] flex items-center justify-center text-gray-500 text-center">
                            <div>
                                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <p className="text-lg font-medium mb-1">No Student Selected</p>
                                <p className="text-sm">Select a student to view their attendance calendar</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <AttendanceDurationDialog
                open={showDurationDialog}
                onClose={() => {
                    setShowDurationDialog(false);
                    setComments('');
                    setDuration(1);
                }}
                onSubmit={() => {
                    setShowDurationDialog(false);
                    setComments('');
                    setDuration(1);
                }}
                initialDuration={duration}
                initialComments={comments}
                initialSlot=""
                hasSlots={slots.length > 0}
                slots={slots}
                isCountHourly={isCountHourly}
            />
        </div>
    );
};

const AttendanceSummary = () => {
    return (
        <ErrorBoundary
            fallback={<div className="p-4 text-red-500">Something went wrong. Please try again later.</div>}
        >
            <AttendanceSummaryContent />
        </ErrorBoundary>
    );
};

export default AttendanceSummary;

